import { Link } from "react-router-dom";
import { Phone, Wrench, ShieldCheck, Award, ArrowRight, MapPin } from "lucide-react";
import { BRAND, SERVICES, SUBURBS } from "@/lib/site";
import SEO from "@/components/SEO";
import ServiceCard from "@/components/ServiceCard";
import OurStory from "@/components/OurStory";
import GetInTouchSection from "@/components/GetInTouchSection";
import CallStrip from "@/components/CallStrip";
import MapEmbed from "@/components/MapEmbed";
import QuoteForm from "@/components/QuoteForm";
import LatestFromBlog from "@/components/LatestFromBlog";

const PILLARS = [
  { icon: Wrench, label: "Expert Sydney Plumbers" },
  { icon: ShieldCheck, label: "24/7 Emergency Service" },
  { icon: Award, label: "Top-Rated Local Service" },
];

export default function HomePage() {
  return (
    <>
      <SEO
        title="24/7 Sydney Plumbers — Hot Water, Gas, Leaks & Emergency"
        description="Flowgrid Plumbing — 24/7 licensed Sydney plumbers covering Western Sydney, Inner West, South-West & CBD. Hot water, gas fitting, leak detection, jet blasting & emergency plumbing. Call 0415 988 997."
        canonicalPath="/"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: BRAND.name,
          url: typeof window !== "undefined" ? window.location.origin : "https://flowgridplumbing.com.au",
          potentialAction: {
            "@type": "SearchAction",
            target: "{search_term_string}",
            "query-input": "required name=search_term_string",
          },
        }}
      />
      {/* HERO */}
      <section
        className="relative isolate overflow-hidden bg-slate-900 text-white"
        data-testid="home-hero"
      >
        <img
          src={BRAND.plumberUrl}
          alt=""
          aria-hidden
          className="absolute inset-0 -z-10 h-full w-full object-cover opacity-40"
        />
        <div className="absolute inset-0 -z-10 bg-gradient-to-r from-slate-900 via-slate-900/85 to-slate-900/40" />

        <div className="fg-container grid items-center gap-12 py-20 md:grid-cols-12 md:py-28">
          <div className="md:col-span-7">
            <h1 className="font-display text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
              Sydney Metro<br />
              Local <span className="text-sky-400">Plumbing</span> Specialist
            </h1>

            <ul className="mt-10 space-y-5">
              {PILLARS.map((p) => {
                const Icon = p.icon;
                return (
                  <li key={p.label} className="flex items-center gap-4">
                    <span className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-blue-600 text-white">
                      <Icon className="h-5 w-5" />
                    </span>
                    <span className="font-display text-xl font-bold uppercase tracking-wide text-white">
                      {p.label}
                    </span>
                  </li>
                );
              })}
            </ul>

            <div className="mt-10">
              <a
                href={BRAND.phoneTel}
                data-testid="hero-call-now-btn"
                className="inline-flex items-center gap-3 rounded-full bg-blue-600 px-8 py-4 text-sm font-bold uppercase tracking-[0.18em] text-white hover:bg-blue-700"
              >
                <Phone className="h-4 w-4" /> Call Us
              </a>
            </div>
          </div>

          {/* Quote form card */}
          <div className="md:col-span-5">
            <div className="overflow-hidden rounded-2xl border border-white/10 bg-white shadow-2xl">
              <div className="bg-slate-900 px-7 py-6 text-center">
                <h2 className="font-display text-2xl font-extrabold uppercase tracking-wider text-white">
                  Get a Quote
                </h2>
              </div>
              <a
                href={BRAND.phoneTel}
                className="block bg-white px-7 py-4 text-center font-bold text-slate-900 transition-colors hover:bg-slate-50"
                data-testid="hero-quote-phone-link"
              >
                Plumbing? Call <span className="text-blue-700">{BRAND.phone}</span>
              </a>
              <div className="border-t border-slate-200 p-6">
                <QuoteForm compact idPrefix="hero" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <OurStory />

      {/* SERVICES — image overlay cards */}
      <section
        className="bg-slate-900 py-20 text-white md:py-24"
        data-testid="home-services"
      >
        <div className="fg-container">
          <div className="text-center">
            <h2 className="font-display text-4xl font-extrabold tracking-tight sm:text-5xl">
              Our <span className="text-yellow-400">Services</span>
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-base text-slate-300">
              We offer a wide range of plumbing services catered to both residential and commercial clients across Sydney Metro.
            </p>
          </div>

          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {SERVICES.map((s) => (
              <ServiceCard key={s.slug} service={s} />
            ))}
          </div>

          <div className="mt-12 flex justify-center">
            <Link
              to="/services"
              data-testid="home-services-all-link"
              className="inline-flex items-center gap-2 rounded-full border-2 border-white/80 px-6 py-3 text-sm font-bold uppercase tracking-wider text-white hover:bg-white hover:text-slate-900"
            >
              View All Services <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* SERVICE AREAS — kept (our local SEO advantage vs Pinnacle) */}
      <section className="bg-white py-20 md:py-24" data-testid="home-service-areas">
        <div className="fg-container">
          <div className="text-center">
            <h2 className="font-display text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl">
              Service <span className="text-blue-600">all Sydney</span>
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-base text-slate-600">
              Wherever you are across Sydney, your local Flowgrid plumber is on call.
            </p>
          </div>

          <div className="mt-12 grid gap-8 md:grid-cols-12">
            <div className="md:col-span-7">
              <MapEmbed height={420} />
            </div>
            <div className="md:col-span-5">
              <ul className="grid grid-cols-2 gap-2">
                {SUBURBS.map((s) => (
                  <li key={s.slug}>
                    <Link
                      to={`/service-areas/${s.slug}`}
                      data-testid={`home-suburb-card-${s.slug}`}
                      className="group flex items-center gap-3 rounded-lg border border-slate-200 bg-white px-4 py-3 transition-colors hover:border-sky-300"
                    >
                      <MapPin className="h-4 w-4 text-sky-600" />
                      <span className="font-bold text-slate-900 group-hover:text-blue-700">
                        {s.name}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
              <Link
                to="/service-areas"
                data-testid="home-all-areas-link"
                className="mt-6 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-blue-700 hover:text-blue-900"
              >
                See all areas <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <CallStrip />
      <LatestFromBlog />
      <GetInTouchSection />
    </>
  );
}
